import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  hasManagedOAuthReturn,
  MANAGED_OAUTH_RETURN_PARAM,
} from '@/modules/credentials/oauth-resume';
import { useOAuthCallbackError } from '@/modules/integrations/hooks/use-oauth-callback-error';
import {
  clearStaleTriggerConnectionIntent,
  consumeTriggerConnectionIntent,
  readTriggerConnectionIntent,
  type TTriggerConnectionIntentKind,
} from '../utils/trigger-connection-intent';

/**
 * Which trigger dialog, if any, this page load owes a reopen to, so a caller
 * can mount it before the data it is normally gated on has arrived. Read once
 * on the first render, since {@link useResumeTriggerConnection} clears the
 * intent as it consumes it.
 *
 * @returns The trigger kind awaiting a resume, or undefined
 */
export function usePendingTriggerResume():
  | TTriggerConnectionIntentKind
  | undefined {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const [pendingKind] = useState(() => {
    if (!searchParams.get('data') && !searchParams.get('error')) {
      return undefined;
    }
    const intent = readTriggerConnectionIntent();
    return intent?.pathname === pathname ? intent.kind : undefined;
  });

  return pendingKind;
}

/** What the dialog needs to put itself back the way the user left it. */
export interface IResumedTriggerConnection {
  integrationName: string;
  integrationVersion?: string;
  /** The connection the user just signed in for; undefined when it failed. */
  connectionId?: string;
}

interface IUseResumeTriggerConnectionParams {
  /** Which trigger dialog is asking. Only its own intent is consumed. */
  kind: TTriggerConnectionIntentKind;
  onResume: (resumed: IResumedTriggerConnection) => void;
}

/**
 * Reopens a trigger dialog the OAuth redirect unmounted, pairing the stored
 * intent with the `?data=<connection_id>` the callback appends. The params are
 * then stripped so a refresh doesn't reopen the dialog a second time.
 *
 * @param params - Trigger kind to resume and the handler that restores state
 */
export function useResumeTriggerConnection({
  kind,
  onResume,
}: IUseResumeTriggerConnectionParams): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const showOAuthCallbackError = useOAuthCallbackError();
  const hasResumedRef = useRef(false);

  // eslint-disable-next-line no-restricted-syntax -- reason: the OAuth return is an external event delivered as a URL param on mount
  useEffect(() => {
    if (hasResumedRef.current) return;

    const connectionId = searchParams.get('data');
    const callbackError = searchParams.get('error');
    if (!connectionId && !callbackError) {
      clearStaleTriggerConnectionIntent(pathname);
      return;
    }

    const intent = consumeTriggerConnectionIntent(kind, pathname);
    if (!intent) return;

    hasResumedRef.current = true;
    // A managed-credential sign-in brings back a credential's id; its
    // connection is created by the return handler, which reports any error.
    const isManagedReturn = hasManagedOAuthReturn(searchParams);

    if (callbackError && !isManagedReturn) {
      showOAuthCallbackError(callbackError, 'useResumeTriggerConnection');
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('data');
    nextParams.delete('error');
    nextParams.delete(MANAGED_OAUTH_RETURN_PARAM);
    setSearchParams(nextParams, { replace: true });

    onResume({
      integrationName: intent.integrationName,
      integrationVersion: intent.integrationVersion,
      connectionId:
        callbackError || isManagedReturn
          ? undefined
          : (connectionId ?? undefined),
    });
  }, [
    kind,
    pathname,
    searchParams,
    setSearchParams,
    showOAuthCallbackError,
    onResume,
  ]);
}
